/* eslint-disable max-lines */
import {
  Button,
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import isAdmin from '../../../helpers/isAdmin';
import isParent from '../../../helpers/isParent';
import ExpirationDatesModal from './ExpirationDatesModal';
import ChangePasswordModal from './ChangePasswordModal';
import AttendancesService from '../../../services/Attendances';
import dateInterval from '../../../helpers/dateInterval';
import getTimeCardInfo from '../../../helpers/getTimeCardInfo';
import { confirmAlert } from 'react-confirm-alert';
import EmployeeChangeStatusConfirm from './EmployeeChangeStatusConfirm';
import isClassroom from '../../../helpers/isClassroom';
import isEducationDirectorAdminAssistant from '../../../helpers/isEducationDirectorAdminAssistant';

interface ProfileRightBarProps {
  reload: boolean;
  handleReload: (reload: boolean) => void;
  currentUser: any;
  isUserHimself: boolean;
  handleActivatedEmployee: (employeeId: string) => void;
}

const ProfileRightBar = ({
  reload: _,
  handleReload: __,
  currentUser,
  isUserHimself = false,
  handleActivatedEmployee,
}: ProfileRightBarProps) => {
  const { id } = useParams<{ id: string }>();
  const { token, user: loggedUser, childcareEnabled }: any = useContext(AuthContext);
  const isParentUser = isParent(currentUser);
  const [expirationDatesModal, setExpirationDatesModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [regularWorked, setRegularWorked] = useState(0);
  const [overtimeWorked, setOvertimeWorked] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);

  const isClassroomUser = isClassroom(currentUser);

  useEffect(() => {
    setIsClockedIn(currentUser?.isClockedIn || false);
    timeCard();
  }, [currentUser]);

  const timeCard = async () => {
    try {
      const currentUserParam = currentUser || {};
      const weekTime = currentUserParam?.employee?.hoursPerWeek || 40;
      const interval = dateInterval('THIS_WEEK');
      const startDateTime = interval.startDateTime;
      const finalDateTime = interval.finalDateTime;
      const result = await AttendancesService(token || '').listByUser(
        id,
        `startDateTime=${startDateTime}&finalDateTime=${finalDateTime}`,
      );
      const data: any[] = result.data;
      const { sumRealWorkTotal, sumOvertime } = getTimeCardInfo(startDateTime, finalDateTime, currentUserParam, data);

      setRegularWorked(sumRealWorkTotal);
      setOvertimeWorked(sumOvertime);
      setRemainingTime(Number(weekTime || 40) - sumRealWorkTotal);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error fetching list');
      return console.error(message);
    }
  };

  const confirmChangestatus = (e: any, id: string) => {
    e.preventDefault();

    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <EmployeeChangeStatusConfirm id={id} handleActivatedEmployee={handleActivatedEmployee} onClose={onClose} />
        );
      },
    });
  };

  const isCurrentUserStaffOrTeacher = (e: any) => {
    return ['Staff', 'Teachers'].includes(e?.group?.name);
  };

  return (
    <>
      {!isUserHimself && !isParentUser && !isClassroomUser && (
        <>
          <Card>
            <CardBody style={{ textAlign: 'center' }}>
              <span className="h2 text-center mx-auto">
                <i
                  className={`far ${
                    isClockedIn ? 'fa-arrow-alt-circle-right text-success' : 'fa-arrow-alt-circle-left text-danger'
                  } mr-1 fa-xl align-baseline font-size-25`}
                />{' '}
                {isClockedIn ? 'Clocked In' : 'Clocked Out'}
              </span>
            </CardBody>
          </Card>
          {!(isEducationDirectorAdminAssistant(loggedUser) && !isCurrentUserStaffOrTeacher(currentUser)) && (
            <Button
              className={`btn-block mt-4 ml-0`}
              color="warning"
              size="md"
              onClick={() => setExpirationDatesModal(true)}
            >
              <i className="fas fa-calendar-alt"></i> Expiration Dates
            </Button>
          )}
          {['HIRED', 'BLOCKED'].includes(currentUser?.employee?.status) ||
          ['HIRED', 'BLOCKED'].includes(currentUser?.status) ? (
            <UncontrolledDropdown nav className="w-100">
              <DropdownToggle className="pr-0" nav style={{ paddingLeft: '0' }}>
                <Button className={`btn-block mt-4 ml-0 text-white bg-warning`} size="md">
                  <i className="fas fa-user mr-2"></i> {currentUser?.employee?.status}
                </Button>
              </DropdownToggle>
              <DropdownMenu
                className="dropdown-menu-arrow w-100"
                right
                style={{ transform: 'translate3d(0px, 75px, 0px)' }}
              >
                <DropdownItem
                  className="w-100 text-center"
                  onClick={(e) => {
                    confirmChangestatus(e, currentUser?.employee?.id);
                  }}
                >
                  <span>Active</span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          ) : (
            <Button
              className={`btn-block mt-4 ml-0 text-white`}
              style={{
                background: currentUser?.employee?.status === 'ACTIVE' ? '#009c22' : '#f5365c',
                pointerEvents: 'none',
                opacity: 1,
              }}
              size="md"
              disabled
            >
              <i className="fas fa-user mr-2"></i> {currentUser?.employee?.status}
            </Button>
          )}

          {currentUser?.employee?.id && childcareEnabled ? (
            <Card className="mt-4">
              <Table responsive>
                <thead>
                  <tr>
                    <th className="text-center border-top-0" colSpan={2}>
                      Time Card (Current Week)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Regular Worked</th>
                    <td>{regularWorked}</td>
                  </tr>
                  <tr>
                    <th scope="row">Overtime Worked</th>
                    <td>{overtimeWorked}</td>
                  </tr>
                  <tr>
                    <th scope="row">Remaining</th>
                    <td>{remainingTime}</td>
                  </tr>
                </tbody>
              </Table>
            </Card>
          ) : (
            <></>
          )}
          <ExpirationDatesModal
            userId={id}
            isOpen={expirationDatesModal}
            toggleModal={() => setExpirationDatesModal(!expirationDatesModal)}
          />
        </>
      )}
      {isAdmin(loggedUser) || loggedUser?.id === currentUser?.id ? (
        <>
          <Button className={`btn-block mt-4 ml-0`} color="danger" size="md" onClick={() => setPasswordModal(true)}>
            <i className="fas fa-key"></i> Change Password
          </Button>
          <ChangePasswordModal
            userId={id}
            isOpen={passwordModal}
            isAdmin={isAdmin(loggedUser)}
            toggleModal={() => setPasswordModal(!passwordModal)}
          />
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default ProfileRightBar;
